/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import { HOC } from 'formsy-react';
import SelectField from 'material-ui/SelectField';
import AutoComplete from 'material-ui/AutoComplete';
import MenuItem from 'material-ui/MenuItem';
import naturalCompare from 'natural-compare-lite';
import SelectorChips from './SelectorChips';
import Spinner from '../Spinner/Spinner';
import { dropDownStyle, common, autoComp } from '../../../theme/AllocateBaseTheme';
import _ from '../../../utils/lodash';
import { lowerFirstLetter, removeSpaces } from '../../../utils/helpers';
import globals from '../../../globals/communityGlobals';

const styles = {
  menuItemSelectOpt: { fontSize: '14px' },
  selectorChip: { fontSize: '13px' },
  choiceComponent: { width: '100%' },
};
class Selector extends React.Component {
  state = { searchText: '', isDirty: false, callArgs: [] };
  componentDidMount() {
    const { isFetchRequired, getOptions } = this.props;
    if (isFetchRequired) {
      getOptions();
    }
  }

  componentWillReceiveProps = nextProps => {
    const { originalValue, autoComplete, isFetchRequired, getOptions } = nextProps;
    if (autoComplete && originalValue) {
      this.setState({ searchText: '' });
    }

    if (
      isFetchRequired &&
      (this.state.callArgs.length !== nextProps.callArgs.length ||
        Object.values(this.props.callArgs).join(',') !== Object.values(nextProps.callArgs).join(','))
    ) {
      this.setState({ callArgs: nextProps.callArgs });
      getOptions();
    }
  };

  componentDidUpdate = () => {
    const { isDirty } = this.state;
    const {
      options,
      getValue,
      isFetching,
      setValue,
      value: propsValue,
      multiple,
      hasDefaultValue,
      showSelectorChip,
    } = this.props;
    const value = isDirty ? getValue() : propsValue;
    const filteredOptions = hasDefaultValue ? [{ id: -1, name: 'Any' }].concat(options) : options;

    if (!isFetching && filteredOptions && value) {
      if (multiple) {
        if (!value.every(v => filteredOptions.some(o => o.id === v))) {
          const newValue = value.filter(v => filteredOptions.some(o => o.id === v));
          setValue(newValue);
        }
      } else if (!showSelectorChip && !filteredOptions.some(o => o.id === value)) {
        setValue(null);
      }
    }
  };

  changeBuffer = (event, key, indexes) => {
    const { multiple, setValue, options, onChange, getValue, clearable, hasDefaultValue } = this.props;
    const filteredOptions = hasDefaultValue ? [{ id: -1, name: 'Any' }].concat(options) : options;

    let newValue;
    if (multiple) {
      newValue = indexes.length ? indexes : undefined;
    } else {
      const currentValue = getValue();

      if (clearable && indexes === currentValue) {
        newValue = undefined;
      } else {
        newValue = indexes || undefined;
      }
    }

    setValue(newValue);

    this.setState({ isDirty: true });
    const option = filteredOptions && filteredOptions.find(o => o.id === newValue);
    onChange(event, key, newValue, option);
  };

  handleRequestDelete = key => () => {
    const { setValue, getValue, options, onChange } = this.props;
    let newValue = getValue();

    if (newValue.length) {
      newValue = newValue.slice();
      newValue.splice(newValue.indexOf(key), 1);
      setValue(newValue.length ? newValue : undefined);
    } else {
      newValue = null;
      setValue(newValue);
    }

    this.setState({ isDirty: true });
    const option = options && options.find(o => o.id === newValue);
    onChange(event, key, newValue, option);
  };

  handleUpdateInput = searchText => {
    this.setState({
      searchText,
    });
  };

  handleNewRequest = item => {
    const { setValue, getValue, multiple } = this.props;
    let searchText = '';
    if (multiple) {
      const oldValue = getValue() || [];
      setValue([...oldValue, item.id]);
    } else {
      searchText = item.text;
      setValue(item.id);
    }

    this.setState({ searchText, isDirty: true });
  };

  generateItems = () => {
    const { isDirty } = this.state;
    const {
      autoComplete,
      reduxIdentifier,
      options,
      value: propsValue,
      getValue,
      multiple,
      filterOptions,
      mapOptions,
      localeArea,
      hasDefaultValue,
      phoneCallHelperText,
      shouldCompareNatural,
    } = this.props;

    const locale = localeArea ? globals.getArea(localeArea) : {};
    const value = isDirty ? getValue() : propsValue;
    const filteredOptions = hasDefaultValue ? [{ id: -1, name: 'Any' }].concat(options) : options;
    let optionsCopy = filteredOptions ? mapOptions(filterOptions(options)).slice() : [];
    optionsCopy = shouldCompareNatural ? optionsCopy.sort((a, b) => naturalCompare(a.name, b.name)) : optionsCopy;
    const itemTemplateGenerator = ({ id, name, visitType, hasTeamStatus, isActive }) => (
      <MenuItem
        style={styles.menuItemSelectOpt}
        key={`selectOpt_${reduxIdentifier + id + removeSpaces(name)}`}
        value={id}
        primaryText={
          localeArea
            ? locale.getMsg(lowerFirstLetter(name))
            : visitType === 'PhoneCall' && phoneCallHelperText
            ? `${name} (${visitType})`
            : name
        }
      >
        {hasTeamStatus && <span className={isActive ? 'icon-active' : 'icon-inactive'} />}
      </MenuItem>
    );

    if (autoComplete) {
      const autoCompleteOptionsPreFilter =
        value && value.length && multiple ? _.remove(optionsCopy, ({ id }) => !value.includes(id)) : optionsCopy;
      return autoCompleteOptionsPreFilter.map(el => ({
        id: el.id,
        text: el.name,
        value: itemTemplateGenerator(el),
      }));
    }
    return optionsCopy.map(el => itemTemplateGenerator(el));
  };

  render() {
    const { isDirty } = this.state;
    const {
      isFetching,
      options,
      getValue,
      disabled,
      setValidations,
      setValue,
      resetValue,
      hasValue,
      getErrorMessage,
      getErrorMessages,
      isFormDisabled,
      isValid,
      isPristine,
      isFormSubmitted,
      isRequired,
      showRequired,
      showError,
      isValidValue,
      endpoint,
      reduxIdentifier,
      isFetchRequired,
      getOptions,
      validationErrors,
      originalValue,
      value: propsValue,
      autoComplete,
      validationError,
      multiple,
      innerRef,
      onChange,
      clearable,
      validations,
      hasDefaultValue,
      filterOptions,
      mapOptions,
      localeArea,
      callArgs,
      mapValue,
      phoneCallHelperText,
      mapWithoutId,
      setValueValidity,
      shouldCompareNatural,
      showSelectorChip,
      ...restOfProps
    } = this.props;
    const value = isDirty ? getValue() : propsValue;

    const isSpinner = isFetching || !options;

    const itemMap = this.generateItems();

    const choiceComponent = autoComplete ? (
      <AutoComplete
        className="auto-comp-comp"
        dataSource={itemMap}
        filter={AutoComplete.caseInsensitiveFilter}
        onNewRequest={this.handleNewRequest}
        searchText={this.state.searchText}
        disabled={disabled}
        openOnFocus
        fullWidth
        floatingLabelStyle={common.floatingLabelStyle}
        floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
        floatingLabelFocusStyle={common.floatingLabelFocusStyle}
        underlineStyle={common.underlineStyle}
        style={autoComp.style}
        textFieldStyle={autoComp.textFieldStyle}
        menuStyle={dropDownStyle.menuStyle}
        listStyle={autoComp.listStyle}
        inputStyle={autoComp.inputStyle}
        {...restOfProps}
      />
    ) : (
      <SelectField
        className="select-field-comp"
        value={value}
        onChange={this.changeBuffer}
        disabled={disabled}
        multiple={multiple}
        fullWidth
        floatingLabelStyle={common.floatingLabelStyle}
        floatingLabelShrinkStyle={common.floatingLabelShrinkStyle}
        floatingLabelFocusStyle={common.floatingLabelFocusStyle}
        underlineStyle={common.underlineStyle}
        style={dropDownStyle.style}
        iconStyle={dropDownStyle.iconStyle}
        labelStyle={dropDownStyle.labelStyle}
        menuStyle={dropDownStyle.menuStyle}
        inputStyle={dropDownStyle.inputStyle}
        {...restOfProps}
      >
        {itemMap}
      </SelectField>
    );
    // width 100% ensures proper flex wrapping for the chips on IE browsers
    return isSpinner ? (
      <Spinner />
    ) : (
      <div style={styles.choiceComponent}>
        {choiceComponent}
        {Array.isArray(value) && options && options.length > 0 && value && (
          <SelectorChips
            style={styles.selectorChip}
            options={mapOptions(options)}
            value={value}
            handleRequestDelete={this.handleRequestDelete}
            disabled={disabled}
            localeArea={localeArea}
          />
        )}
        {showSelectorChip && options && options.length > 0 && value && (
          <SelectorChips
            style={styles.selectorChip}
            options={mapOptions(options)}
            value={value}
            handleRequestDelete={this.handleRequestDelete}
            disabled={disabled}
            localeArea={localeArea}
            showSelectorChip
          />
        )}
      </div>
    );
  }
}

Selector.defaultProps = {
  required: false,
  value: undefined,
  originalValue: undefined,
  hasDefaultValue: false,
  options: null,
  multiple: false,
  clearable: false,
  isFetchRequired: false,
  disabled: false,
  autoComplete: false,
  localeArea: null,
  validationErrors: {},
  validationError: null,
  validations: {},
  innerRef: () => {},
  onChange: () => {},
  filterOptions: options => options,
  mapOptions: options => options,
  mapValue: value => value,
  callArgs: [],
  phoneCallHelperText: false,
  mapWithoutId: false,
  setValueValidity: () => {},
  shouldCompareNatural: true,
  showSelectorChip: false,
};

const optionPropType = PropTypes.shape({
  id: PropTypes.number,
  value: PropTypes.number,
  name: PropTypes.string,
});

Selector.propTypes = {
  floatingLabelText: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(optionPropType),
  innerRef: PropTypes.func,
  hasDefaultValue: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types
  value: PropTypes.oneOfType([
    // Used by formsy
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.number,
  ]),
  originalValue: PropTypes.oneOfType([
    // Used by formsy
    PropTypes.arrayOf(optionPropType),
    optionPropType,
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.number,
  ]),
  isFetching: PropTypes.bool.isRequired,
  multiple: PropTypes.bool,
  clearable: PropTypes.bool,
  isFetchRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  autoComplete: PropTypes.bool,
  localeArea: PropTypes.string,
  reduxIdentifier: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  mapValue: PropTypes.func,
  // Formsy props:
  setValidations: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  getValue: PropTypes.func.isRequired,
  getOptions: PropTypes.func.isRequired,
  resetValue: PropTypes.func.isRequired,
  hasValue: PropTypes.func.isRequired,
  getErrorMessage: PropTypes.func.isRequired,
  getErrorMessages: PropTypes.func.isRequired,
  isFormDisabled: PropTypes.func.isRequired,
  isValid: PropTypes.func.isRequired,
  isPristine: PropTypes.func.isRequired,
  isFormSubmitted: PropTypes.func.isRequired,
  isRequired: PropTypes.func.isRequired,
  showRequired: PropTypes.func.isRequired,
  showError: PropTypes.func.isRequired,
  isValidValue: PropTypes.func.isRequired,
  // Other propTypes throw a 'marked as required' error for isRequired inside validationErrors ?!
  validationErrors: PropTypes.shape(),
  validationError: PropTypes.string,
  filterOptions: PropTypes.func,
  mapOptions: PropTypes.func,
  validations: PropTypes.shape(),
  // eslint-disable-next-line react/forbid-prop-types
  callArgs: PropTypes.any,
  phoneCallHelperText: PropTypes.bool,
  mapWithoutId: PropTypes.bool,
  setValueValidity: PropTypes.func,
  shouldCompareNatural: PropTypes.bool,
  showSelectorChip: PropTypes.bool,
};

export default HOC(Selector);
